import { Component, Vue, Watch } from 'vue-property-decorator';
import UserService from '@/services/user.service';
import ParticipantService from '@/services/participant.service';
import { store, mutations } from '@/store/store';
import LanguagePicker from '@/components/LanguagePicker/LanguagePicker';
import Loader from '@/components/Loader/Loader';
import MoreInfo from '@/components/MoreInfo/MoreInfo';
import Logo from '@/components/Logo/Logo';
import MobileMenu from '@/components/MobileMenu/MobileMenu';
import Stepper from '@/components/Stepper/Stepper';
import { WatchOptions } from 'vue';
import TokenService from '@/services/token.service';
import { McsisRole } from '@/models/constants/McsisRole';
import { APP_VERSION, getCorrespondenceLanguagesBySu } from '@/config/app.config';
import { loadLanguageAsync } from '@/plugins/i18n';
import LocalStoreService from '@/store/local.store';
import ModalTemplate from '@/components/ModalTemplate/ModalTemplate';
import Contact from '@/components/Contact/Contact';
import { isProLoginActivatedFromSU } from '@/config/app.config';
import { InclusionTypes } from '@/models/VariousEnum';

@Component({
  components: {
    LanguagePicker,
    Loader,
    MoreInfo,
    Logo,
    MobileMenu,
    Stepper,
    ModalTemplate,
    Contact,
  },
})
export default class App extends Vue {
  // Properties
  public mobileMenuOpen: boolean = false;
  public proLoginActivated: boolean = false;
  public currentStepNumber: number = 0;
  public maxVisitedStepNumber: number = 0;
  public env = process.env.NODE_ENV;
  public languageChoices: boolean = true;
  private get suName() { return this.$route.params.screening; }
  private showUpdateModal: boolean = false;
  

  public get navigationSteps(): string[] {
    if (store.state.hasCustomConsent) {
      return [
        'inclusion',
        'questionnary',
        'medicalcontent',
        'consent',
        'recap',
        'confirmation',
      ];
    } else {
      return [
        'inclusion',
        'questionnary',
        'medicalcontent',
        'recap',
        'confirmation',
      ];
    }
  }

  public created() {
    this.checkAppVersion();
  }

  public mounted() {
    window.addEventListener('load', () => {
      // run after everything is in-place
      mutations.setIsAppLoaded(true);
      this.setup();

      // Check if Pro Login is activated for the current SU
      this.proLoginActivated = isProLoginActivatedFromSU(this.suName);

      // Remove the locale from $i18n in order to force a "refresh" of the texts
      // by setting the former locale.
      const oldLocale = this.$i18n.locale;
      this.$i18n.locale = '';
      this.$i18n.locale = oldLocale;
    });
  }

  get userName() {
    return this.authenticated ? LocalStoreService.getItem(LocalStoreService.localItemKeys.proUsername) : '';
  }
  get identified() { return store.state.isIdentified; }
  get authenticated() { return store.state.isAuthenticated; }
  get isDossierOpened() { return store.state.isDossierOpened; }

  get refreshTrys() {
    return parseInt(LocalStoreService.getItem(LocalStoreService.localItemKeys.appVersionTrys), 10) || 0;
  }
  set refreshTrys(value: number) {
    LocalStoreService.setItem(LocalStoreService.localItemKeys.appVersionTrys, value.toString());
  }

  /**
   * Check the app version when the App is first loaded
   * and force the refresh if the version is different
   * from the app-version.json file on the server.
   */
  public checkAppVersion(): void {
    fetch('/app-version.json')
      .then((res) => res.json())
      .then((data) => {
        mutations.setAppVersion({
          isLatest: data.version === APP_VERSION,
          lastCheckDate: new Date(),
        });
        if (!store.state.appVersion.isLatest) {
          if (this.refreshTrys > 1) {
            this.showUpdateModal = true;
          } else {
            this.refreshTrys++;
            window.location.reload();
          }
        } else {
          // Reset the values
          this.refreshTrys = 0;
        }
      })
      .catch((err) => {
        throw(err);
      });
  }

  public hideUpdateModal(): void {
    this.showUpdateModal = false;
  }

  /**
   * Setup the application on first loading
   */
  public async setup() {
    TokenService.setXsrfTokenInHeader();

    if (TokenService.tokenExists()) {
      const isTokenValid = await TokenService.isTokenValid()
      .then((res) => {
        return res;
      })
      .catch((err) => {
        return false;
      });
      if (isTokenValid) {
        let routePrefix = this.suName;
        if (!routePrefix) {
          // Get the route prefix from the connected participant/pro user
          routePrefix = await UserService.getUserRoutePrefix();
        }
        this.$isInRole(McsisRole.Participant)
          .then(async (isInRole: boolean) => {
            if (isInRole) {
              await ParticipantService.logout();
              // Force a redirect to the login page if it's not already the case
              // (by default the router will do it, but in case we change the behaviour
              // we fallback here)
              if (!!this.$route.name && this.$route.name !== 'login') {
                const inclusionType = this.$route.path.substring(this.$route.path.lastIndexOf('/') + 1);
                if (!(inclusionType in InclusionTypes)) {
                  this.$router.push({
                    name: 'login',
                    params: {
                      screening: routePrefix,
                    },
                  });
                }

              }
            } else {
              this.setAuthenticated(true).then(
                () => {
                  // redirect to login participant in order to open the participant dossier
                  if (!!this.$route.name && this.$route.name !== 'login') {
                    this.$router.push({
                      name: 'login',
                      params: {
                        screening: routePrefix,
                      },
                    });
                  }
                });
            }
          });
      } else {
        // In case a token exists but is no longer valid we force a logout from the
        // frontend to be sure to clean all data
        const routePrefix = this.suName;
        this.$isInRole(McsisRole.Participant)
          .then(async (isInRole: boolean) => {
            if (isInRole) {
              ParticipantService.logoutFromFrontend();
              if (!!this.$route.name && this.$route.name !== 'login') {
                this.$router.push({
                  name: 'login',
                  params: {
                    screening: routePrefix,
                  },
                });
              }
            } else {
              UserService.logoutFromFrontend();
              if (!!this.$route.name && this.$route.name !== 'pro-login') {
                this.$router.push({
                  name: 'pro-login',
                  params: {
                    screening: routePrefix,
                  },
                });
              }
            }
          });
      }
    }
  }

  public openingDossier(): void {
    // set maVisitedStepNumber to 0 in order to avoid stepping unchecked step
    this.maxVisitedStepNumber = 0;
  }

  public selectStep(step: number): void {
    if (this.$route.params.partId !== undefined && step <= this.maxVisitedStepNumber) {
      this.$router.push({
        name: this.navigationSteps[step - 1], // step is 1-based
        params: {
          screening: this.$route.params.screening,
          partId: this.$route.params.partId,
        },
      });
    }
  }

  /**
   * Update the current step number (by the selected step number sent from the grand-child component)
   * only if it is smaller than the maximum visited step (prevent from seeing step not already seen).
   */
  public onUpdateCurrentStepNumber(step: number): void {
    if (step <= this.maxVisitedStepNumber) {
      this.currentStepNumber = step;
      this.selectStep(step);
    }
  }
  // triggered from hamburger
  protected toggleMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }
  // triggered from mobile menu links
  protected closeMobMenu(): void {
    this.mobileMenuOpen = false;
  }

  protected setIdentified(status: boolean): void {
    mutations.setIsIdentified(status);
  }

  protected async setAuthenticated(status: boolean): Promise<void> {
    mutations.setIsAuthenticated(status);
  }

  protected get inclusionType(): string { return store.state.inclusionType; }
  // Get loading state from store
  protected get isLoading(): boolean { return store.state.dataLoading; }

  /**
   * Logs the participant or the user out of the app
   * @param routeName The route name where to be redirected after the logout
   */
  protected async logout(routeName: string|null): Promise<void> {
    if (this.authenticated) {
      await UserService.logout();
    }
    if (this.identified) {
      await ParticipantService.logout();
    }

    if (this.inclusionType !== '') {
      if (this.inclusionType === InclusionTypes.fit) {
        this.$router.push({ name: 'login-fit' });
      } else if (this.inclusionType === InclusionTypes.colo) {
        this.$router.push({ name: 'login-colo'});
      } else if (this.inclusionType === InclusionTypes.refusal) {
        this.$router.push({ name: 'login-refusal'});
      }
    } else if (routeName) {
      this.$router.push({ name: routeName });
    }
  }

  protected addPopupMessageBeforeUnload(): void {
    if (window.onbeforeunload) {
      return;
    }

    window.onbeforeunload = (ev: any) => {
      const e = ev || window.event;
      const warningText = this.$i18n.t(`msgs.notSavedDataWarningMessage`).toString();

      // For IE and Firefox
      if (e) {
        e.returnValue = warningText;
      }

      // For Safari, Chrome
      return warningText;
    };
  }

  protected removePopupMessageBeforeUnload(): void {
    if (window.onbeforeunload) {
      window.onbeforeunload = null;
    }
  }

  @Watch('$route.name', { immediate: true, deep: true } as WatchOptions)
  protected onRouteNameChanged(to: string, from: string) {
    if (from !== to && to !== undefined && this.navigationSteps.indexOf(to) !== -1) {
      this.currentStepNumber = this.navigationSteps.indexOf(to) + 1; // currentStepNumber is 1-based
      if (this.$route.name === 'consent') {
        this.maxVisitedStepNumber = this.currentStepNumber;
      } else {
        this.maxVisitedStepNumber = Math.max(this.currentStepNumber, this.maxVisitedStepNumber);
      }
    }

    if (to !== undefined) {
      if ((this.navigationSteps.indexOf(to) !== -1 && to !== 'confirmation') || to === 'alternativeQuestionnaire') {
        this.addPopupMessageBeforeUnload();
      } else {
        this.removePopupMessageBeforeUnload();
      }
    }

    if (to === undefined) {
      this.removePopupMessageBeforeUnload();
    }
  }

  
  /**
   * Le su est récupéré depuis $route mais $route n'est pas forcément chargé avant 
   * App.ts, c'est pour ça qu'il nous faut un watcher
   * @date 17.08.2023 - 11:26:21
   *
   * @protected
   * @param {string} suName - Nom du su récupéré via $route
   * @param {string} oldSuName - L'ancien su si il y a un changement d'url
   */
  @Watch('suName', { immediate: true, deep: true } as WatchOptions)
  protected onLocaleChanged(suName: string, oldSuName: string) {
    if (oldSuName !== suName && suName !== undefined) {
      mutations.setIsLanguagesLoaded(false);

      // On récupère le ou les langues disponible pour le su passé en paramètre
      const supportedLanguages = getCorrespondenceLanguagesBySu(this.$route.params.screening);
      
      // Gestion de l'affiche du LanguagePicker -> on ne l'affiche pas si le su a une seule langue
      if(supportedLanguages.length == 1){
        this.languageChoices = false;
      }

      // Load languages by SU
      supportedLanguages.forEach((lang: string) => {
        loadLanguageAsync(lang, suName).then(() => { mutations.setIsLanguagesLoaded(true); });
      });
    }
  }
}
